/**
 * Production-safe logger utility.
 *
 * Replaces raw console.* calls across the app.
 * - In production: suppresses debug/info logs, redacts PII from errors.
 * - In development: logs everything for debugging convenience.
 */

const IS_PROD = process.env.NODE_ENV === 'production';

const SENSITIVE_KEYS = [
  'password',
  'token',
  'authorization',
  'email',
  'phone',
  'credit',
  'secret',
  'cookie',
  'session',
];

/**
 * Recursively redact sensitive fields from an object.
 * Returns a shallow-safe copy — never mutates the original.
 */
const sanitize = data => {
  if (!data || typeof data !== 'object') {
    return data;
  }
  if (Array.isArray(data)) {
    return data.map(sanitize);
  }
  const copy = {};
  for (const [key, value] of Object.entries(data)) {
    const lower = key.toLowerCase();
    if (SENSITIVE_KEYS.some(s => lower.includes(s))) {
      copy[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      copy[key] = sanitize(value);
    } else {
      copy[key] = value;
    }
  }
  return copy;
};

export const logger = {
  /** Debug-level: completely silenced in production. */
  debug: (msg, data) => {
    if (!IS_PROD) {
      // eslint-disable-next-line no-console
      console.log(`[DEBUG] ${msg}`, data !== undefined ? sanitize(data) : '');
    }
  },

  /** Info-level: silenced in production. */
  info: (msg, data) => {
    if (!IS_PROD) {
      // eslint-disable-next-line no-console
      console.info(`[INFO] ${msg}`, data !== undefined ? sanitize(data) : '');
    }
  },

  /** Warning-level: always shown, data is sanitized. */
  warn: (msg, data) => {
    // eslint-disable-next-line no-console
    console.warn(`[WARN] ${msg}`, data !== undefined ? sanitize(data) : '');
  },

  /** Error-level: always shown. In production only the message is logged (no data). */
  error: (msg, data) => {
    if (IS_PROD) {
      // eslint-disable-next-line no-console
      console.error(`[ERROR] ${msg}`);
    } else {
      // eslint-disable-next-line no-console
      console.error(`[ERROR] ${msg}`, data !== undefined ? sanitize(data) : '');
    }
  },
};

export default logger;
